/*
* Created on Mar 03, 2021
* @author: Adam Cheng <adamcheng@qnap.com>
*/
import {useEffect, useRef, useState} from 'react';
import TextInputComponent from "../../common/Form/TextInputComponent";
import {useQuery} from "@tanstack/react-query";
import drop_drop_down_svg from "../../../static/img/utils/ic-arrow-drop-down.svg";
import remove_svg from "../../../static/img/utils/ic-remove.svg";
import ic_checked from "../../../static/img/utils/ic-checked.svg";
import {OverlayScrollbarsComponent} from "overlayscrollbars-react";
import SubmitButtonComponent from "../../common/Form/SubmitButtonComponent";
import {useTranslation} from "react-i18next";
import {useOrganizationApi} from "../../../hook/OrganizationAPIHook";


const OrganizationGroupFormComponent = ({input, setInput, newOrgId, dispatcher, state}) => {
    const {t} = useTranslation();
    const {OrganizationAPI} = useOrganizationApi();
    const [page, setPage] = useState({limit: 100, offset: 0})
    const [refresh, setRefresh] = useState(1);
    const [groupList, setGroupList] = useState({});
    const [isEditing, setIsEditing] = useState(document.querySelectorAll('#groupTableBody input').length === 0);
    const nameRef = useRef(null);
    const descriptionRef = useRef(null);

    const onCheckClicked = async () => {
        if (!input.name.value) return;
        const name = input.name.value;
        const description = input.description.value;
        try {
            const res = await OrganizationAPI.post(`organization/${newOrgId}/group`, null, {name, description});
            if (!res) return;

            setGroupList(prev => {
                const id = res.result.group_id;
                const newList = {...prev, [id]: {name, description, organization_group_id: id}};
                dispatcher({type: 'getUpdate', payload: {groups: Object.values(newList)}});
                return newList;
            });
            setIsEditing(false);
            setInput(init_input);
        } catch (e) {
            state.alert(true, 'error', e?.response?.data?.code, e?.response?.data?.message, 0, {
                group_name: name,
                interpolation: {
                    prefix: '{',
                    suffix: '}',
                    escapeValue: false
                }
            });
        }
    }

    const onDeleteClicked = async (key) => {
        await OrganizationAPI.delete(`organization/${newOrgId}/group/${key}`);
        setGroupList(prev => {
            const newList = {...prev};
            delete newList[key];
            dispatcher({type: 'getUpdate', payload: {groups: Object.values(newList)}});
            return newList;
        });
        setInput(init_input);
    }

    const addNewGroupEntry = () => {
        setIsEditing(true);
        setTimeout(() => {
            nameRef.current.focus();
        }, 100);
    }

    const {status, data, error, isFetching} = useQuery({
        queryKey: [`organization/${newOrgId}/group`, page, refresh],
        queryFn: async () => {
            const group_list = await OrganizationAPI.get(`organization/${newOrgId}/group`, {...page});
            dispatcher({type: 'getUpdate', payload: {groups: group_list.result}});
            if (group_list?.result) {
                setGroupList(prev => {
                    const newList = {...prev};
                    group_list.result.forEach(g => {
                        newList[g.organization_group_id] = g;
                    });
                    return newList;
                });
            }
            return group_list.result;
        }
    });

    const init_input = {
        name: {value: '', error: null, required: true},
        description: {value: '', error: null, required: false}
    }

    useEffect(() => {
        if (!input.name) {
            setInput(init_input);
        }
    }, []);

    return (
        <>
            <div>{t('@The owner group is the system default group, and members in this group can edit organization information and invite new members. You can also create groups in the purpose of granting specifical administration privilege.@')}</div>
            <div className="toolbar">
                <button type="button" className="q-btn q-btn-default q-btn-small q-btn-round-corner" onClick={addNewGroupEntry} disabled={isEditing}>{t('@Create group@')}</button>
            </div>

            <div className="flexbox-table">
                <div className="flexbox-thead">
                    <div className="thead-cell !w-38">
                        <div className="sort sort-disabled">
                            <span className="sort-label">{t('@Group name@')}</span>
                        </div>
                    </div>
                    <div className="thead-cell !w-45">
                        <div className="sort sort-disabled">
                            <span className="sort-label">{t('@Description@')}</span>
                        </div>
                    </div>
                    <div className="thead-cell !w-17">
                        <div className="sort sort-disabled">
                            <span className="sort-label">{t('@Action@')}</span>
                        </div>
                    </div>
                </div>
                <div id="groupTableBody" className="flexbox-tbody overlay-scroll os-host-flexbox">
                    {
                        Object.entries(groupList).length > 0 &&
                        Object.entries(groupList).map(([key, group]) => (
                            <div className="flexbox-trow" key={key}>
                                <div className="tbody-cell !w-38">
                                    <span className="tag">{group.name}</span>
                                </div>
                                <div className="tbody-cell !w-45">{group.description}</div>
                                <div className="tbody-cell !w-17">
                                    <div className="act-btn-group">
                                        <div className="act-btn delete-btn" onClick={async () => {await onDeleteClicked(key)}}>
                                            <img src={remove_svg}/>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    }
                    {
                        isEditing &&
                        <div className="flexbox-trow editing">
                            <div className="tbody-cell !w-38">
                                <div className="comp-input w-100">
                                    <input type="text" placeholder="Group name" className="w-100" ref={nameRef} value={input?.name?.value}
                                           onChange={e => {
                                               setInput(prev => ({...prev, name: {value: e.target.value, error: null, required: true}}))
                                           }}/>
                                </div>
                            </div>
                            <div className="tbody-cell !w-45">
                                <div className="comp-input w-100">
                                    <input type="text" placeholder="Description" className="w-100" ref={descriptionRef} value={input?.description?.value}
                                           onChange={e => {
                                                  setInput(prev => ({...prev, description: {value: e.target.value, error: null, required: false}}))
                                           }}/>
                                </div>
                            </div>
                            <div className="tbody-cell !w-17">
                                <div className="act-btn-group">
                                    <div className="act-btn confirm-btn" onClick={async e => {await onCheckClicked()}} disabled={!input?.name?.value}>
                                        <img src={ ic_checked }/>
                                    </div>
                                    <div className="act-btn cancel-btn" onClick={() => {
                                        setIsEditing(false);
                                        setInput(init_input);
                                    }}>
                                        <img src={remove_svg} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    }
                </div>
            </div>
        </>
    );
};

export default OrganizationGroupFormComponent;